import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { MdAssignment, MdHistory } from 'react-icons/md';
import DataTable from 'react-data-table-component';
import { fetchContracts } from '../../../services/Http/Api';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';

const ContractsSettings = () => {
    const { t } = useTranslation();
    const [contracts, setContracts] = useState([]);
    const [activeContract, setActiveContract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('terms');

    useEffect(() => {
        const loadContracts = async () => {
            try {
                const contractsPayload = await fetchContracts();
                const allContracts = Array.isArray(contractsPayload)
                    ? contractsPayload
                    : (contractsPayload?.contracts || []);
                const activeContractData = Array.isArray(contractsPayload)
                    ? (allContracts.find((contract) => contract?.active) || null)
                    : (contractsPayload?.active_contract || null);

                setContracts(allContracts);
                setActiveContract(activeContractData);
                setLoading(false);
            } catch (err) {
                toast.error(t('Settings.Contracts.error') + ': ' + err.message);
                setLoading(false);
            }
        };
        loadContracts();
    }, [t]);

    const formatNumber = (value, digits = 2) => {
        if (value === null || value === undefined || value === '') {
            return Number(0).toFixed(digits);
        }
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? Number(0).toFixed(digits) : numericValue.toFixed(digits);
    };

    const formatPercent = (value) => `${formatNumber(value, 2)}%`;

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '48px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
                fontSize: '14px',
                color: '#111827',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb',
                    cursor: 'default',
                },
            },
        },
    };

    const historyColumns = [
        {
            name: t('Settings.Contracts.startDate'),
            selector: row => row.start_date,
            sortable: true,
        },
        {
            name: t('Settings.Contracts.endDate'),
            selector: row => row.end_date || t('Settings.Contracts.ongoing'),
            sortable: true,
        },
        {
            name: t('Settings.Contracts.status'),
            selector: row => row.active ? t('Settings.Contracts.active') : t('Settings.Contracts.inactive'),
            sortable: true,
            cell: row => (
                <StatusBadge $active={row.active}>
                    {row.active ? t('Settings.Contracts.active') : t('Settings.Contracts.inactive')}
                </StatusBadge>
            )
        }
    ];

    if (loading) return <LoadingMessage>{t('Settings.loading')}</LoadingMessage>;

    return (
        <Container>
            <SectionLayout>
                <SectionTabs>
                    <SectionTab $active={activeSection === 'terms'} onClick={() => setActiveSection('terms')}>
                        <MdAssignment size={18} />
                        {t('Settings.Contracts.contractTerms')}
                    </SectionTab>
                    <SectionTab $active={activeSection === 'history'} onClick={() => setActiveSection('history')}>
                        <MdHistory size={18} />
                        {t('Settings.Contracts.contractHistory')}
                    </SectionTab>
                </SectionTabs>
                <SectionContent>
                    {activeSection === 'terms' && (
                        <StyledCard title={t('Settings.Contracts.contractTerms')}>
                            {activeContract ? (
                                <DetailsGrid>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.startDate')}</DetailLabel>
                                        <DetailValue>{activeContract.start_date}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.endDate')}</DetailLabel>
                                        <DetailValue>{activeContract.end_date || t('Settings.Contracts.ongoing')}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.status')}</DetailLabel>
                                        <DetailValue>
                                            <StatusBadge $active={activeContract.active}>
                                                {activeContract.active ? t('Settings.Contracts.active') : t('Settings.Contracts.inactive')}
                                            </StatusBadge>
                                        </DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.profitPercentageFee')}</DetailLabel>
                                        <DetailValue>{formatPercent(activeContract.transaction_fee)}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.crossSellCommission')}</DetailLabel>
                                        <DetailValue>{formatPercent(activeContract.cross_sell_commission_percentage)}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.fixedTransactionFee')}</DetailLabel>
                                        <DetailValue>{formatNumber(activeContract.fixed_transaction_fee)}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.emailNotificationFee')}</DetailLabel>
                                        <DetailValue>{formatNumber(activeContract.email_notification_fee)}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.smsNotificationFee')}</DetailLabel>
                                        <DetailValue>{formatNumber(activeContract.sms_notification_fee)}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.pushNotificationFee')}</DetailLabel>
                                        <DetailValue>{formatNumber(activeContract.push_notification_fee)}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.monthlySubscriptionFee')}</DetailLabel>
                                        <DetailValue>{formatNumber(activeContract.monthly_subscription_fee)}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.promosFeePerDay')}</DetailLabel>
                                        <DetailValue>{formatNumber(activeContract.promos_fee_per_day)}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>{t('Settings.Contracts.shareInitialPrice')}</DetailLabel>
                                        <DetailValue>{activeContract.shares_initial_price ? t('Settings.Contracts.active') : t('Settings.Contracts.inactive')}</DetailValue>
                                    </DetailItem>
                                </DetailsGrid>
                            ) : (
                                <EmptyState>{t('Settings.Contracts.noContracts')}</EmptyState>
                            )}
                        </StyledCard>
                    )}

                    {activeSection === 'history' && (
                        <StyledCard title={t('Settings.Contracts.contractHistory')} noPadding>
                            <DataTable
                                columns={historyColumns}
                                data={contracts}
                                customStyles={customStyles}
                                pagination
                                noDataComponent={<EmptyState>{t('Settings.Contracts.noContracts')}</EmptyState>}
                            />
                        </StyledCard>
                    )}
                </SectionContent>
            </SectionLayout>
        </Container>
    );
};

export default ContractsSettings;

// Styled Components
const Container = styled.div`
    width: 100%;
`;

const SectionLayout = styled.div`
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 1.5rem;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const SectionTabs = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SectionTab = styled.button`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid ${({ $active }) => ($active ? '#701a82' : 'transparent')};
    background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : 'transparent')};
    color: ${({ $active }) => ($active ? '#701a82' : '#6b7280')};
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;

    &:hover {
        background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : '#f9fafb')};
        color: #701a82;
    }
`;

const SectionContent = styled.div`
    flex: 1;
`;

const DetailsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.25rem;
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const DetailLabel = styled.div`
    font-size: 0.8125rem;
    color: #6b7280;
    font-weight: 500;
`;

const DetailValue = styled.div`
    font-size: 0.9375rem;
    color: #111827;
    font-weight: 500;
`;

const StatusBadge = styled.span`
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    background: ${({ $active }) => $active ? '#dcfce7' : '#fee2e2'};
    color: ${({ $active }) => $active ? '#166534' : '#991b1b'};
    display: inline-block;
`;

const EmptyState = styled.div`
    padding: 2rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
`;
